import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, TooltipProvider } from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { listKinnserSyncAgencies } from '../../services/api';
import { useStore } from '../../store';
import {
  agencyDataFillRows,
  matchingAgencyDataFillRows,
  type AgencyDataFillRow,
} from './agencyDataFillRows';
import { AgencyDataFillRail } from './AgencyDataFillRail';
import { lastWorkedOn, recentAgencies } from './agencyRecents';
import { AgencyDataFillPane } from './AgencyDataFillPane';

/**
 * The Agency Data Fill tab: a rail you pick an agency from, and a pane.
 *
 * Read-only. The screen answers "has this agency got its Kinnser data, and
 * what does it hold" -- for all of them at once, which until now meant opening
 * one agency at a time. Reading Kinnser again, and correcting what it sent,
 * are their own changes.
 *
 * Side by side rather than a table read across: sixteen values per clinic do
 * not fit in columns, and a pane that scrolls on its own means picking an
 * agency cannot move the page under the pointer.
 */

type Filter = 'recent' | 'all';

const AgencyDataFillPanel: React.FC = () => {
  const showError = useStore(useShallow(state => state.showError));
  const [rows, setRows] = useState<AgencyDataFillRow[]>([]);
  const [loading, setLoading] = useState(true);
  // Held apart from an empty list. A failed load leaves no rows, and without
  // this the screen would answer "no agencies" to a request that never got an
  // answer -- the opposite of what happened.
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Null until somebody picks, so the default below can change as the data
  // does without overriding a choice they made.
  const [filter, setFilter] = useState<Filter | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      // throwOnError, or the client hands the error back as a value and this
      // catch never runs: `data?.agencies ?? []` would read a 403 as "none".
      const response = await listKinnserSyncAgencies({ throwOnError: true });
      setRows(agencyDataFillRows(response.data?.agencies ?? []));
    } catch (error) {
      console.error('Failed to load the agency list', error);
      setFailed(true);
      showError('Unable to load agencies. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const recents = useMemo(
    () => recentAgencies(rows, new Date(), lastWorkedOn),
    [rows]
  );
  // Recent by default, but only once there is something in it: an empty list
  // behind a tab somebody has to notice is worse than the long one.
  const activeFilter: Filter =
    filter ?? (recents.agencyIds.size > 0 ? 'recent' : 'all');

  // A search widens past Recent. Intersecting the two answered a name somebody
  // typed with "No agency matches that search" when the agency existed and
  // simply had not been touched lately -- the screen denying something it holds.
  const searching = search.trim() !== '';
  const visible = useMemo(
    () =>
      matchingAgencyDataFillRows(rows, search).filter(
        row =>
          activeFilter === 'all' ||
          searching ||
          recents.agencyIds.has(row.agencyId)
      ),
    [rows, search, activeFilter, searching, recents]
  );

  // The selected agency, or nothing when the search has hidden it. Reading it
  // out of the visible rows rather than holding the row itself keeps one copy:
  // a reload replaces every row object, and a held one would go stale.
  const selected = visible.find(row => row.agencyId === selectedId) ?? null;

  if (loading) {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading agencies&hellip;
      </div>
    );
  }

  if (failed) {
    return (
      <div className='flex flex-col items-start gap-2 sm:flex-row sm:items-center'>
        <p className='text-sm text-muted-foreground'>
          Could not load agencies.
        </p>
        <Button size='sm' variant='outline' onClick={() => void load()}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    // The app has no global provider -- every consumer wraps its own -- and the
    // rail's per-row tooltip throws without one.
    <TooltipProvider>
      {/* The page scrolls on a phone and the two panes share a fixed height
          from md up. Pinning the height on mobile let flexbox divide it by
          content, and the rail's long list took almost all of it -- leaving the
          pane, which is the point of the screen, a letterbox. */}
      <div className='flex flex-col gap-4 md:h-[calc(100vh-16rem)] md:min-h-[24rem] md:flex-row'>
        {/* Full width on a phone, a fixed rail beside the pane from md up. */}
        {/* shrink-0, or the pane squeezes it. The grid sets a min-width of
            16rem plus 15rem per clinic, and a flex item will not shrink below
            its content's minimum -- so without this the rail narrowed as the
            chosen agency gained clinics. */}
        <div className='flex max-h-[60vh] min-h-0 w-full flex-col rounded-md border md:max-h-none md:w-80 md:shrink-0'>
          <div className='space-y-2 border-b p-2'>
            <Input
              inputSize='sm'
              className='w-full'
              placeholder='Search agencies'
              aria-label='Search agencies'
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
            {/* Two buttons rather than a tab strip: there are two, they sit in
                a 20rem rail, and the pressed one is the state. */}
            <div className='flex gap-1'>
              <Button
                size='sm'
                variant={activeFilter === 'recent' ? 'secondary' : 'ghost'}
                aria-pressed={activeFilter === 'recent'}
                onClick={() => setFilter('recent')}
              >
                Recent
              </Button>
              <Button
                size='sm'
                variant={activeFilter === 'all' ? 'secondary' : 'ghost'}
                aria-pressed={activeFilter === 'all'}
                onClick={() => setFilter('all')}
              >
                All agencies
              </Button>
            </div>
            {/* Recent stays pressed while the results reach past it, so say so
                rather than leaving the wider list unexplained. */}
            {activeFilter === 'recent' && searching && (
              <p className='text-xs text-muted-foreground'>
                Searching all agencies.
              </p>
            )}
          </div>
          {visible.length === 0 ? (
            <p className='p-3 text-sm text-muted-foreground'>
              {rows.length === 0
                ? 'No agencies yet.'
                : searching
                  ? 'No agency matches that search.'
                  : 'Nothing worked on lately.'}
            </p>
          ) : (
            <AgencyDataFillRail
              rows={visible}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          )}
        </div>

        {/* min-w-0 so the grid's own overflow-x-auto does the scrolling,
            rather than the pane growing to fit it. */}
        <div className='flex min-h-0 w-full min-w-0 flex-1 flex-col rounded-md border'>
          {selected ? (
            /* Keyed, so picking another agency builds a new pane. The pane
               holds a pull in flight and the reasons saves were refused, and
               without this they carried over -- a refusal for the agency you
               just left, sitting under the name of the one you just opened. */
            <AgencyDataFillPane
              key={selected.agencyId}
              row={selected}
              onPulled={() => void load()}
            />
          ) : (
            <p className='p-4 text-sm text-muted-foreground'>
              Pick an agency to see what it holds.
            </p>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default AgencyDataFillPanel;
